import { useEffect, useSyncExternalStore } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useLang } from '@/i18n/useLang'
import { isLocalizedZone } from '@/i18n/lang-path'
import { applyMeta, langOfPath, metaFor, withUnreadCount, type PageMeta } from './seo'

/**
 * Маленькое внешнее хранилище: значение и подписчики. Два таких ниже —
 * собственная мета страницы и число непрочитанных; `useSeo` пишет документ из
 * ОБОИХ, поэтому ни смена языка, ни новое сообщение не затирают имя страницы.
 */
function createSlot<T>(initial: T) {
  let value = initial
  const listeners = new Set<() => void>()
  return {
    get: () => value,
    set(next: T) {
      if (Object.is(next, value)) return
      value = next
      listeners.forEach((listener) => listener())
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

const pageMeta = createSlot<PageMeta | null>(null)
const unread = createSlot(0)

/**
 * Держит мету документа в согласии с маршрутом.
 *
 * Вызывается ОДИН раз в корне: заголовок страницы — свойство адреса, а не
 * компонента, и раскладывать этот вызов по экранам значит забыть его на
 * следующем.
 *
 * Язык меты — язык АДРЕСА на витрине (`/ru/...`), а вне её — язык интерфейса:
 * у кабинета и моделлера русского адреса нет, и по адресу они всегда были
 * «английскими» — русский кабинет подписывал вкладки английским слоганом.
 */
export function useSeo(): void {
  const path = useRouterState({ select: (s) => s.location.pathname })
  const { lang } = useLang()
  const own = useSyncExternalStore(pageMeta.subscribe, pageMeta.get, pageMeta.get)
  const count = useSyncExternalStore(unread.subscribe, unread.get, unread.get)
  useEffect(() => {
    const pageLang = isLocalizedZone(path) ? langOfPath(path) : lang
    applyMeta(withUnreadCount(own ?? metaFor(path, pageLang), count, path), path, pageLang)
  }, [path, lang, own, count])
}

/**
 * Своё имя страницы — для тех, у кого оно есть только во время работы:
 * карточка психолога, схема по ссылке, «такой страницы нет». Побеждает
 * табличное, пока страница на экране; при уходе табличное возвращается само.
 */
export function usePageMeta(meta: PageMeta | null): void {
  // Зависимость — сам объект: вызывающая сторона обязана его мемоизировать
  // (иначе новый объект на каждый рендер переписывал бы мету бесконечно).
  useEffect(() => {
    if (!meta) return undefined
    pageMeta.set(meta)
    return () => {
      if (pageMeta.get() === meta) pageMeta.set(null)
    }
  }, [meta])
}

/**
 * Непрочитанные — в заголовок вкладки: «(2) Сообщения — HUB4PSY». Зовёт рамка
 * зоны, у которой это число уже есть (кабинет — практика, зона клиента — свои
 * переписки); уходя из зоны, число снимается.
 */
export function useUnreadInTitle(count: number): void {
  useEffect(() => {
    unread.set(count)
    return () => unread.set(0)
  }, [count])
}
